import { Box, Grid, Typography } from '@mui/material';
import * as yup from 'yup';
import { useQueryClient } from 'react-query';
import {
  SparkForm,
  SparkFormImageUploader,
  SparkTextField,
  SparkDropdown,
  SparkFormTaggedAutoComplete,
  SparkBasicFormFooter,
} from 'sharedComponents/SparkForm';
import { useIndustries } from 'hooks/BusinessIndustries/useIndustries';
import { useBusinessStages } from 'hooks/BusinessStages/useBusinessStages';
import { useCountries } from 'hooks/Countries/useCountries';
import { useGeographicMarkets } from 'hooks/GeographicMarkets/useGeographicMarkets';
import { useLegalStructures } from 'hooks/LegalStructures/useLegalStructures';
import { useMarketApproaches } from 'hooks/MarketApproaches/useMarketApproaches';
import { getInitialStringValue } from 'utilities/form';
import { useUpdateCompanyOverview } from 'hooks/CompanyOverview/useUpdateCompanyOverview';
import { useUpdateWorkSpace } from 'hooks/Workspace/useUpdateWorkSpace';
import {
  CompanyOverview,
  CompanyOverviewMarketApproach,
} from 'types/CompanyOverview';
import { Workspace } from 'types/Workspace';
import { MarketApproach } from 'types/MarkertApproaches';
import { useResize } from 'hooks/common';
import {
  useBatchAddMarketApproaches,
  useBatchRemoveMarketApproaches,
} from 'hooks/batchOperations';

type EditCompanyInfoFromSettingsValues = {
  companyName: string | undefined;
  countryID: string | Number;
  industryID: string | Number;
  coreOffering: string | undefined;
  companyStageID: string | Number;
  imageURL: string;
  legalStructureID: string | Number;
  marketApproaches: string[] | undefined;
  geographicMarketID: string | Number;
};

const validationSchema = yup.object({
  companyName: yup.string().required('Required'),
  countryID: yup.string().required('Required'),
  industryID: yup.string().required('Required'),
  coreOffering: yup.string().required('Required'),
  companyStageID: yup.string().required('Required'),
  legalStructureID: yup.string().required('Required'),
  marketApproaches: yup.array().required('Required'),
  geographicMarketID: yup.string().required('Required'),
});

type CompanyOverviewFormProps = {
  companyOverview: CompanyOverview | undefined;
  workspace: Workspace | undefined;
  marketApproaches: MarketApproach[] | undefined;
  companyOverviewMarketApproach: CompanyOverviewMarketApproach[] | undefined;
};

const CompanyOverviewForm = ({
  companyOverview,
  workspace,
  marketApproaches,
  companyOverviewMarketApproach,
}: CompanyOverviewFormProps) => {
  const { isMobile } = useResize();
  const queryClient = useQueryClient();
  const { dropdownOptions: countries, isLoading: isLoadingCountries } =
    useCountries();
  const { dropdownOptions: industries, isLoading: isLoadingIndustries } =
    useIndustries();
  const {
    dropdownOptions: legalStructures,
    isLoading: isLoadingLegalStructures,
  } = useLegalStructures();
  const {
    dropdownOptions: geographicMarkets,
    isLoading: isLoadingGeographicMarkets,
  } = useGeographicMarkets();
  const {
    dropdownOptions: marketApproachesOptions,
    isLoading: isLoadingMarketApproaches,
  } = useMarketApproaches();
  const {
    dropdownOptions: businessStages,
    isLoading: isLoadingBusinessStages,
  } = useBusinessStages();

  const { mutateAsync: updateCompanyOverview } = useUpdateCompanyOverview(
    companyOverview?.id,
    { shouldInvalidatedOnSuccess: false }
  );
  const { mutateAsync: updateWorkspace } = useUpdateWorkSpace(workspace?.id);
  const { mutateAsync: addMarketApproach } = useBatchAddMarketApproaches(
    companyOverview?.id,
    { shouldInvalidatedOnSuccess: false }
  );
  const { mutateAsync: removeMarketApproach } = useBatchRemoveMarketApproaches(
    companyOverview?.id,
    { shouldInvalidatedOnSuccess: false }
  );

  const initialValues = {
    companyName: getInitialStringValue(
      companyOverview?.attributes?.companyName
    ),
    countryID: getInitialStringValue(companyOverview?.attributes?.countryID),
    industryID: getInitialStringValue(
      companyOverview?.attributes?.businessIndustryID
    ),
    coreOffering: getInitialStringValue(
      companyOverview?.attributes?.coreOffering
    ),
    imageURL: getInitialStringValue(workspace?.attributes.imageURL),
    companyStageID: getInitialStringValue(
      companyOverview?.attributes?.businessStageID
    ),
    legalStructureID: getInitialStringValue(
      companyOverview?.attributes?.legalStructureID
    ),
    marketApproaches:
      marketApproaches?.map((marketApproach) => marketApproach.id) || [],
    geographicMarketID: getInitialStringValue(
      companyOverview?.attributes?.geographicMarketID
    ),
  };

  const handleSubmitCompanyInfo = async (
    values: EditCompanyInfoFromSettingsValues
  ) => {
    const {
      companyName,
      countryID,
      industryID,
      coreOffering,
      companyStageID,
      legalStructureID,
      geographicMarketID,
      imageURL,
      marketApproaches,
    } = values || {};
    await updateCompanyOverview({
      companyName,
      countryID: +countryID,
      businessIndustryID: +industryID,
      coreOffering,
      businessStageID: +companyStageID,
      legalStructureID: +legalStructureID,
      geographicMarketID: +geographicMarketID,
    });
    await updateWorkspace({ imageURL });
    if (companyOverviewMarketApproach?.length) {
      await removeMarketApproach({
        marketApproachIDs: companyOverviewMarketApproach.map(
          (marketApproach) => marketApproach.id
        ),
      });
    }
    addMarketApproach({
      marketApproachIDs:
        marketApproaches?.map((marketApproachID) => marketApproachID) || [],
    });
    queryClient.invalidateQueries(['companyOverview', companyOverview?.id]);
  };

  const onImageUpload = async (imageURL: string) => {
    await updateWorkspace({ imageURL });
  };

  return (
    <Grid container>
      <Typography
        variant='h5'
        sx={{
          fontFamily: 'Mont-semibold',
        }}
      >
        Basic Info
      </Typography>
      <Box
        width='100%'
        bgcolor='white'
        borderRadius='20px'
        border={(theme) => `1px solid ${theme.palette.borderColor}`}
      >
        <SparkForm<EditCompanyInfoFromSettingsValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSave={handleSubmitCompanyInfo}
          containerProps={{ padding: 2, spacing: 2 }}
        >
          <SparkFormImageUploader
            name='imageURL'
            containerName='public-assets'
            onUpload={onImageUpload}
          />
          <SparkTextField
            name='companyName'
            label='Company Name'
            placeholder='Enter Company Name'
          />

          <SparkDropdown
            name='countryID'
            label='Country'
            children={[...countries]}
          />

          <SparkDropdown
            name='industryID'
            label='Industry'
            children={[...industries]}
          />
          <SparkDropdown
            name='geographicMarketID'
            label='Geographic Market'
            children={[...geographicMarkets]}
            helpText='Select the primary area where your business operates to target
customers.'
          />
          <SparkTextField
            name='coreOffering'
            label='Core Offering'
            size={12}
            multiline
            rows={4}
            placeholder='e.g. Interactive online wellness platform.'
            helpText='Briefly describe your main product or service in one sentence'
          />
          <SparkDropdown
            name='companyStageID'
            label='Company Stage'
            children={[...businessStages]}
          />

          <SparkDropdown
            name='legalStructureID'
            label='Legal Structure'
            children={[...legalStructures]}
          />

          <SparkFormTaggedAutoComplete
            name='marketApproaches'
            label='Market Approach'
            placeholder='Please select up to 3'
            maxSelections={3}
            options={marketApproachesOptions}
          />

          <SparkBasicFormFooter
            shouldRenderResetButton={true}
            primaryButtonStyles={{ width: '200px' }}
            resetButtonStyles={{ width: '200px' }}
            resetButtonText='Cancel'
          />
        </SparkForm>
      </Box>
    </Grid>
  );
};

export default CompanyOverviewForm;
